import { useState } from "react";
import { Download, Trash2, Pencil, Check, X, Sparkles, Loader2, Eye, Coins } from "lucide-react";
import { FileTypeIcon, fmtBytes, fmtDate } from "./docUtils";
import { previewKind } from "./DocumentPreviewDialog";

// Eén documentregel: naam (klik = voorbeeld of download), label (inline bewerkbaar), meta, acties.
export default function DocumentRow({ doc, canManage, onDownload, onPreview, onDelete, onRelabel, onExtractLiquidity, busy }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(doc.label || "");
  const canPreview = Boolean(previewKind(doc.filename));
  const isPdf = /\.pdf$/i.test(doc.filename || "");

  const commit = async () => {
    setEditing(false);
    if (draft.trim() !== (doc.label || "")) await onRelabel(doc, draft.trim());
  };

  return (
    <li className="px-3 py-2.5 flex items-center gap-3 hover:bg-muted/40 transition-colors" data-testid={`doc-row-${doc.id}`}>
      <FileTypeIcon filename={doc.filename} className="w-5 h-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <button type="button" onClick={() => (canPreview ? onPreview(doc) : onDownload(doc))} disabled={busy} title={canPreview ? "Bekijken" : "Downloaden"}
          data-testid={`doc-name-${doc.id}`}
          className="font-bold text-sm truncate max-w-full text-left hover:text-primary hover:underline disabled:opacity-50">
          {doc.filename}
        </button>
        <div className="flex items-center gap-2 flex-wrap text-[11px] text-muted-foreground">
          {editing ? (
            <span className="inline-flex items-center gap-1">
              <input autoFocus value={draft} maxLength={120} onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setDraft(doc.label || ""); setEditing(false); } }}
                data-testid={`doc-label-input-${doc.id}`} placeholder="Label, bijv. Jaarverslag 2024"
                className="border-2 border-foreground bg-background px-1.5 py-0.5 text-xs w-56 focus:outline-none focus:ring-2 focus:ring-primary" />
              <button type="button" onClick={commit} aria-label="Label opslaan" data-testid={`doc-label-save-${doc.id}`} className="p-0.5 text-positive"><Check className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => { setDraft(doc.label || ""); setEditing(false); }} aria-label="Annuleren" className="p-0.5"><X className="w-3.5 h-3.5" /></button>
            </span>
          ) : (
            <button type="button" disabled={!canManage} onClick={() => setEditing(true)} data-testid={`doc-label-${doc.id}`}
              className={`inline-flex items-center gap-1 ${canManage ? "hover:text-foreground" : "cursor-default"} ${doc.label ? "text-foreground/80" : "italic"}`}>
              {doc.label || (canManage ? "Label toevoegen" : "")}{canManage && <Pencil className="w-3 h-3 opacity-60" />}
            </button>
          )}
          {doc.source === "import" && (
            <span className="inline-flex items-center gap-1 border border-primary text-primary px-1 py-px font-display uppercase tracking-widest text-[9px] font-bold" data-testid={`doc-import-badge-${doc.id}`}>
              <Sparkles className="w-2.5 h-2.5" /> Import
            </span>
          )}
          <span className="font-mono-num">{fmtBytes(doc.size)}</span>
          <span>·</span>
          <span className="truncate max-w-[160px]" title={doc.uploaded_by_name}>{doc.uploaded_by_name || "—"}</span>
          <span>·</span>
          <span className="font-mono-num">{fmtDate(doc.created_at)}</span>
        </div>
      </div>
      {canPreview && (
        <button type="button" onClick={() => onPreview(doc)} title="Bekijken" aria-label="Bekijken"
          data-testid={`doc-preview-${doc.id}`}
          className="p-1.5 border-2 border-foreground hover:bg-primary hover:border-primary hover:text-primary-foreground transition-colors">
          <Eye className="w-4 h-4" />
        </button>
      )}
      {canManage && isPdf && onExtractLiquidity && (
        <button type="button" onClick={() => onExtractLiquidity(doc)} disabled={busy} title="Kasstroom uit dit jaarverslag halen (AI)" aria-label="Kasstroom uit dit jaarverslag halen"
          data-testid={`doc-extract-liquidity-${doc.id}`}
          className="p-1.5 border-2 border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground transition-colors disabled:opacity-40">
          <Coins className="w-4 h-4" />
        </button>
      )}
      <button type="button" onClick={() => onDownload(doc)} disabled={busy} title="Downloaden" aria-label="Downloaden"
        data-testid={`doc-download-${doc.id}`}
        className="p-1.5 border-2 border-foreground hover:bg-foreground hover:text-background transition-colors disabled:opacity-40">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
      </button>
      {canManage && (
        <button type="button" onClick={() => onDelete(doc)} title="Verwijderen" aria-label="Verwijderen"
          data-testid={`doc-delete-${doc.id}`}
          className="p-1.5 border-2 border-destructive text-destructive hover:bg-destructive hover:text-background transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </li>
  );
}
